import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { toast } from "sonner";
import { useTranslation } from 'react-i18next';

export function OnboardingDialog() {
  const [open, setOpen] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const { user } = useAuth();
  const { t } = useTranslation();

  const steps = [
    {
      title: t('onboarding.welcome.title'),
      description: t('onboarding.welcome.description'),
      icon: "👋",
    },
    {
      title: t('onboarding.projects.title'),
      description: t('onboarding.projects.description'),
      icon: "📋",
    },
    {
      title: t('onboarding.equipment.title'),
      description: t('onboarding.equipment.description'),
      icon: "🎥",
    },
    {
      title: t('onboarding.crew.title'),
      description: t('onboarding.crew.description'),
      icon: "👥",
    },
    {
      title: t('onboarding.ready.title'),
      description: t('onboarding.ready.description'),
      icon: "🚀",
    },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeOnboarding();
    }
  };

  const completeOnboarding = async () => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ onboarding_completed: true })
        .eq("id", user?.id);

      if (error) throw error;
      
      setOpen(false);
      toast.success(t('onboarding.complete.success'));
    } catch (error) {
      console.error("Error updating onboarding status:", error);
      toast.error(t('onboarding.complete.error'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[425px]">
        <div className="grid gap-6 py-4">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="text-4xl">{steps[currentStep].icon}</div>
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold tracking-tight">
                {steps[currentStep].title}
              </h2>
              <p className="text-sm text-muted-foreground">
                {steps[currentStep].description}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex-1 flex gap-1">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    index === currentStep
                      ? "bg-primary"
                      : index < currentStep
                      ? "bg-primary/50"
                      : "bg-muted"
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="w-[100px]"
            >
              {t('onboarding.skip')}
            </Button>
            <Button onClick={handleNext} className="w-[100px]">
              {currentStep === steps.length - 1 ? t('onboarding.get_started') : t('onboarding.next')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}